import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, AtSign, BarChart3, CalendarDays, Check, ChevronLeft, ChevronRight,
  Clock3, Eye, Facebook, ImagePlus, Instagram, Linkedin, Link2, Loader2, MessageCircle,
  Music2,
  PauseCircle, Pencil, PlayCircle, RefreshCw, Save, Send, Settings2, ShieldCheck,
  Trash2, Upload, XCircle, Youtube,
} from "lucide-react";
import { axiosInstance } from "@/App";

const STATUS_TABS = [
  ["draft", "Drafts"],
  ["awaiting_approval", "Awaiting approval"],
  ["scheduled", "Scheduled"],
  ["published", "Published"],
  ["failed", "Failed"],
];

const WORKSPACE_TABS = [
  ["workflow", "Workflow", Send],
  ["calendar", "Content calendar", CalendarDays],
  ["previews", "Platform previews", Eye],
  ["analytics", "Campaign analytics", BarChart3],
  ["comments", "Comment review", MessageCircle],
  ["brand", "Brand settings", Settings2],
];

const EMPTY_POST = {
  title: "",
  caption: "",
  campaign: "General",
  media_url: "",
  link_url: "",
  scheduled_for: "",
  internal_notes: "",
  platforms: ["facebook", "instagram"],
};

const formatDate = (value) => value ? new Date(value).toLocaleString(undefined, {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
}) : "Publish after approval";

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const getError = (error, fallback) => error?.response?.data?.detail || fallback;

const Panel = ({ title, detail, action, children, className = "" }) => (
  <section className={`overflow-hidden rounded-2xl border border-white/[.08] bg-[#121214] ${className}`}>
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[.06] px-5 py-4">
      <div>
        <h2 className="font-bold text-white">{title}</h2>
        {detail && <p className="mt-1 text-xs leading-relaxed text-gray-500">{detail}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const PlatformPill = ({ platform }) => {
  const Icon = platform === "facebook" ? Facebook : Instagram;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${platform === "facebook" ? "bg-blue-500/15 text-blue-300" : "bg-pink-500/15 text-pink-300"}`}>
      <Icon className="h-3 w-3" /> {platform === "facebook" ? "Facebook" : "Instagram"}
    </span>
  );
};

const PROVIDER_PRESENTATION = {
  youtube: { label: "YouTube", Icon: Youtube, color: "text-red-400" },
  x: { label: "X", Icon: AtSign, color: "text-white" },
  linkedin: { label: "LinkedIn", Icon: Linkedin, color: "text-blue-400" },
  tiktok: { label: "TikTok", Icon: Music2, color: "text-pink-300" },
};

const AccountConnectionCard = ({ provider, value = {}, busy, onConnect, onDisconnect }) => {
  const presentation = PROVIDER_PRESENTATION[provider] || { label: provider, Icon: Link2, color: "text-gray-300" };
  const { label, Icon, color } = presentation;
  const account = value.username ? `@${value.username}` : value.account_name;
  return (
    <article className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold">{label}</p>
            <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-wider ${value.connected ? "bg-green-500/15 text-green-300" : value.configured ? "bg-amber-500/15 text-amber-300" : "bg-gray-500/15 text-gray-500"}`}>
              {value.connected ? "Connected" : value.configured ? "Ready" : "Setup needed"}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-gray-400">{account || (value.configured ? "No account connected" : `Add ${(value.credential_names || []).join(" + ")} in AWS`)}</p>
          <p className="mt-2 text-[10px] leading-relaxed text-gray-600">{value.review_note}</p>
          {value.last_error && <p className="mt-2 text-[10px] leading-relaxed text-red-300">{value.last_error}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {value.connected && <button type="button" onClick={() => onDisconnect(provider, label)} disabled={Boolean(busy)} className="rounded-lg border border-white/10 px-3 py-2 text-[11px] font-bold text-gray-300 hover:bg-white/5 disabled:opacity-40">Disconnect</button>}
            <button type="button" onClick={() => onConnect(provider)} disabled={!value.configured || Boolean(busy)} className="rounded-lg bg-blue-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-blue-500 disabled:opacity-40">
              {busy === `connect-${provider}` ? "Opening..." : value.connected ? "Refresh" : "Connect"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

const PlatformPreview = ({ platform, draft, settings }) => {
  const isFacebook = platform === "facebook";
  const Icon = isFacebook ? Facebook : Instagram;
  return (
    <article className="mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/10 bg-[#18181b] shadow-xl">
      <div className="flex items-center gap-3 p-4">
        <span className={`flex h-10 w-10 items-center justify-center rounded-full ${isFacebook ? "bg-blue-600" : "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400"}`}>
          <Icon className="h-5 w-5 text-white" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{settings?.brand_name || "ShowMeLive"}</p>
          <p className="text-[11px] text-gray-500">Sponsored preview - Just now</p>
        </div>
      </div>
      {draft.media_url ? (
        <div className={`${isFacebook ? "aspect-[1.91/1]" : "aspect-square"} bg-black`}>
          <img src={draft.media_url} alt="Post preview" className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className={`${isFacebook ? "aspect-[1.91/1]" : "aspect-square"} flex items-center justify-center bg-gradient-to-br from-blue-950 to-[#101012] text-gray-600`}>
          <ImagePlus className="h-10 w-10" />
        </div>
      )}
      <div className="space-y-3 p-4">
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-200">
          {draft.caption || "Your caption will appear here."}
        </p>
        {draft.link_url && <p className="truncate text-xs font-semibold text-blue-400">{draft.link_url}</p>}
        <div className="flex items-center justify-between border-t border-white/[.07] pt-3 text-xs text-gray-500">
          <span>Like</span><span>Comment</span><span>{isFacebook ? "Share" : "Send"}</span>
        </div>
      </div>
    </article>
  );
};

const SocialMediaCommandCenter = () => {
  const [data, setData] = useState(null);
  const [workspace, setWorkspace] = useState("workflow");
  const [status, setStatus] = useState("draft");
  const [draft, setDraft] = useState(EMPTY_POST);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [commentFilter, setCommentFilter] = useState("unreviewed");
  const [brandDraft, setBrandDraft] = useState({});

  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setBusy("load");
    setError("");
    try {
      const response = await axiosInstance.get("/admin/social/command-center");
      setData(response.data);
      setBrandDraft(response.data?.settings || {});
    } catch (requestError) {
      setError(getError(requestError, "The Social Media Command Center could not be loaded."));
    } finally {
      if (!quiet) setBusy("");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const socialStatus = params.get("social");
    const provider = params.get("provider") || "social account";
    const reason = params.get("reason");
    load().then(() => {
      if (socialStatus === "connected") setNotice(`${provider === "meta" ? "Meta Business" : provider.charAt(0).toUpperCase() + provider.slice(1)} connected successfully.`);
      if (socialStatus === "error" || socialStatus === "expired") setError(reason || "The social account connection could not be completed.");
    });
    if (socialStatus) {
      params.delete("social");
      params.delete("provider");
      params.delete("reason");
      const query = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    }
    // The OAuth query is consumed once when the command center mounts.
  }, []);

  const posts = data?.posts || [];
  const visiblePosts = useMemo(() => posts.filter((post) => post.status === status), [posts, status]);
  const filteredComments = useMemo(() => (data?.comments || []).filter((comment) => (
    commentFilter === "all" || comment.review_status === commentFilter
  )), [commentFilter, data?.comments]);

  const calendarPosts = useMemo(() => posts.filter((post) => {
    const value = post.scheduled_for || post.published_at;
    if (!value || !["scheduled", "published"].includes(post.status)) return false;
    const date = new Date(value);
    return date.getFullYear() === calendarMonth.getFullYear() && date.getMonth() === calendarMonth.getMonth();
  }).sort((a, b) => new Date(a.scheduled_for || a.published_at) - new Date(b.scheduled_for || b.published_at)), [calendarMonth, posts]);

  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const togglePlatform = (platform) => setDraft((current) => ({
    ...current,
    platforms: current.platforms.includes(platform)
      ? current.platforms.filter((item) => item !== platform)
      : [...current.platforms, platform],
  }));

  const resetComposer = () => {
    setDraft(EMPTY_POST);
    setEditingId(null);
  };

  const editPost = (post) => {
    setDraft({
      title: post.title || "",
      caption: post.caption || "",
      campaign: post.campaign || "General",
      media_url: post.media_url || "",
      link_url: post.link_url || "",
      scheduled_for: toInputDateTime(post.scheduled_for),
      internal_notes: post.internal_notes || "",
      platforms: post.platforms || [],
    });
    setEditingId(post.id);
    setWorkspace("workflow");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const postPayload = () => ({
    ...draft,
    title: draft.title.trim(),
    caption: draft.caption.trim(),
    campaign: draft.campaign.trim() || "General",
    media_url: draft.media_url || null,
    link_url: draft.link_url.trim() || null,
    internal_notes: draft.internal_notes.trim(),
    scheduled_for: draft.scheduled_for ? new Date(draft.scheduled_for).toISOString() : null,
  });

  const savePost = async (event) => {
    event.preventDefault();
    if (!draft.caption.trim() || !draft.platforms.length) return;
    setBusy("save-post");
    setError("");
    try {
      if (editingId) await axiosInstance.put(`/admin/social/posts/${editingId}`, postPayload());
      else await axiosInstance.post("/admin/social/posts", postPayload());
      setNotice(editingId ? "Draft updated." : "Draft created. It must be submitted and approved before publishing.");
      resetComposer();
      setStatus("draft");
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "The draft could not be saved."));
    } finally {
      setBusy("");
    }
  };

  const uploadMedia = async (file) => {
    setBusy("media");
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axiosInstance.post("/admin/social/media", form);
      updateDraft("media_url", response.data?.media_url || "");
    } catch (requestError) {
      setError(getError(requestError, "The image could not be uploaded."));
    } finally {
      setBusy("");
    }
  };

  const runPostAction = async (post, action) => {
    const labels = { submit: "submit this draft for approval", approve: "approve this post", "return-to-draft": "return this post to Draft" };
    if (action === "approve" && !window.confirm(`Approve "${post.title}"? ${paused ? "It will remain scheduled until publishing resumes." : post.scheduled_for ? `It will publish ${formatDate(post.scheduled_for)}.` : "It will publish immediately."}`)) return;
    setBusy(`${post.id}:${action}`);
    setError("");
    try {
      await axiosInstance.post(`/admin/social/posts/${post.id}/${action}`);
      setNotice(`Post ${labels[action] || action} completed.`);
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, `Could not ${labels[action] || action}.`));
      await load({ quiet: true });
    } finally {
      setBusy("");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete the draft "${post.title}"?`)) return;
    setBusy(`${post.id}:delete`);
    try {
      await axiosInstance.delete(`/admin/social/posts/${post.id}`);
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "The post could not be deleted."));
    } finally {
      setBusy("");
    }
  };

  const connectMeta = async () => {
    setBusy("connect-meta");
    setError("");
    try {
      const response = await axiosInstance.get("/admin/social/meta/connect-url");
      window.location.assign(response.data.url);
    } catch (requestError) {
      setError(getError(requestError, "The Meta connection could not be started."));
      setBusy("");
    }
  };

  const disconnectMeta = async () => {
    if (!window.confirm("Disconnect the Meta Business account? Scheduled posts will remain queued but cannot publish until reconnected.")) return;
    setBusy("disconnect-meta");
    try {
      await axiosInstance.delete("/admin/social/meta/connection");
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "Meta could not be disconnected."));
    } finally {
      setBusy("");
    }
  };

  const connectProvider = async (provider) => {
    setBusy(`connect-${provider}`);
    setError("");
    try {
      const response = await axiosInstance.get(`/admin/social/providers/${provider}/connect-url`);
      window.location.assign(response.data.url);
    } catch (requestError) {
      setError(getError(requestError, `The ${provider} connection could not be started.`));
      setBusy("");
    }
  };

  const disconnectProvider = async (provider, label) => {
    if (!window.confirm(`Disconnect the ${label} account from ShowMeLive?`)) return;
    setBusy(`disconnect-${provider}`);
    setError("");
    try {
      await axiosInstance.delete(`/admin/social/providers/${provider}/connection`);
      setNotice(`${label} disconnected.`);
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, `${label} could not be disconnected.`));
    } finally {
      setBusy("");
    }
  };

  const syncMeta = async () => {
    setBusy("sync-meta");
    setError("");
    try {
      const response = await axiosInstance.post("/admin/social/sync");
      setNotice(`Meta sync complete: ${response.data.posts || 0} posts and ${response.data.comments || 0} comments checked.`);
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "Meta analytics and comments could not be synced."));
    } finally {
      setBusy("");
    }
  };

  const toggleEmergencyPause = async () => {
    const paused = Boolean(data?.settings?.emergency_paused);
    if (!paused && !window.confirm("Pause all scheduled social publishing? Already published posts will remain online.")) return;
    setBusy("pause");
    try {
      await axiosInstance.post("/admin/social/emergency-pause", { paused: !paused });
      setNotice(paused ? "Scheduled publishing resumed." : "Emergency pause is active. No scheduled posts will publish.");
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "The emergency publishing setting could not be changed."));
    } finally {
      setBusy("");
    }
  };

  const saveBrand = async (event) => {
    event.preventDefault();
    setBusy("brand");
    try {
      await axiosInstance.patch("/admin/social/brand-settings", {
        brand_name: brandDraft.brand_name,
        brand_voice: brandDraft.brand_voice,
        default_hashtags: brandDraft.default_hashtags,
        default_link_url: brandDraft.default_link_url,
        timezone: brandDraft.timezone,
        approval_required: true,
        active_meta_page_id: brandDraft.active_meta_page_id || data?.providers?.meta?.page_id || null,
      });
      setNotice("Brand settings saved.");
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "Brand settings could not be saved."));
    } finally {
      setBusy("");
    }
  };

  const reviewComment = async (comment, reviewStatus) => {
    setBusy(`${comment.id}:${reviewStatus}`);
    try {
      await axiosInstance.patch(`/admin/social/comments/${comment.id}`, { status: reviewStatus });
      await load({ quiet: true });
    } catch (requestError) {
      setError(getError(requestError, "The comment review status could not be changed."));
    } finally {
      setBusy("");
    }
  };

  if (!data && busy === "load") {
    return <div className="flex min-h-[420px] items-center justify-center text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading Social Media Command Center...</div>;
  }

  const meta = data?.providers?.meta || {};
  const accountPlatforms = data?.account_platforms || ["youtube", "x", "linkedin", "tiktok"];
  const paused = Boolean(data?.settings?.emergency_paused);

  return (
    <div className="space-y-6" data-testid="social-media-command-center">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[.16em] text-blue-400">Growth operations</p>
          <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">Social Media Command Center</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-400">Connect ShowMeLive's official social accounts, then create once, approve deliberately, and publish through approved platform APIs.</p>
        </div>
        <button
          type="button"
          onClick={toggleEmergencyPause}
          disabled={busy === "pause"}
          className={`inline-flex min-h-12 items-center gap-2 rounded-xl border px-5 text-sm font-extrabold transition ${paused ? "border-green-500/30 bg-green-500/15 text-green-300 hover:bg-green-500/20" : "border-red-500/35 bg-red-500/15 text-red-300 hover:bg-red-500/25"}`}
          data-testid="social-emergency-pause"
        >
          {paused ? <PlayCircle className="h-5 w-5" /> : <PauseCircle className="h-5 w-5" />}
          {busy === "pause" ? "Updating..." : paused ? "Resume publishing" : "Emergency pause"}
        </button>
      </div>

      {paused && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><p className="font-extrabold">Scheduled publishing is paused</p><p className="mt-1 text-xs leading-relaxed text-red-200/75">Drafting and approval still work. Nothing new will be sent to Meta until an administrator resumes publishing.</p></div>
        </div>
      )}
      {error && <div className="flex items-start justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/10 p-4 text-sm text-red-300"><span>{error}</span><button type="button" onClick={() => setError("")} aria-label="Dismiss error"><XCircle className="h-4 w-4" /></button></div>}
      {notice && <div className="flex items-start justify-between gap-3 rounded-xl border border-green-500/25 bg-green-500/10 p-4 text-sm text-green-300"><span>{notice}</span><button type="button" onClick={() => setNotice("")} aria-label="Dismiss notice"><Check className="h-4 w-4" /></button></div>}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,.9fr)_minmax(460px,1.1fr)]">
        <Panel title="Meta Business" detail="Approved business publishing for Facebook Pages and linked Instagram professional accounts.">
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <div className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">
              <div className="flex items-center gap-3"><Facebook className="h-5 w-5 text-blue-400" /><div><p className="text-sm font-bold">Facebook</p><p className="text-xs text-gray-500">{meta.page_name || "No Page connected"}</p></div></div>
            </div>
            <div className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">
              <div className="flex items-center gap-3"><Instagram className="h-5 w-5 text-pink-400" /><div><p className="text-sm font-bold">Instagram</p><p className="text-xs text-gray-500">{meta.instagram_username ? `@${meta.instagram_username}` : "No professional account linked"}</p></div></div>
            </div>
          </div>
          {(meta.configuration_warning || meta.last_error || (meta.declined_permissions || []).length > 0) && (
            <div className="space-y-2 px-5 pb-4">
              {meta.configuration_warning && <p className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-[11px] leading-relaxed text-amber-200">{meta.configuration_warning}</p>}
              {meta.last_error && <p className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-[11px] leading-relaxed text-red-200">{meta.last_error}</p>}
              {(meta.declined_permissions || []).length > 0 && <p className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-[11px] leading-relaxed text-red-200">Reconnect and approve: {meta.declined_permissions.join(", ")}</p>}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[.06] px-5 py-4">
            <div>
              <p className={`text-xs font-bold ${meta.connected ? "text-green-400" : "text-amber-300"}`}>{meta.connected ? "Meta Business connected" : meta.configured ? "Connection ready" : "Meta Business credentials required"}</p>
              {meta.connected && <p className="mt-1 text-[10px] text-gray-600">{(meta.available_pages || []).length} manageable Page{(meta.available_pages || []).length === 1 ? "" : "s"} found</p>}
            </div>
            <div className="flex gap-2">
              {meta.connected && <button type="button" onClick={disconnectMeta} disabled={busy === "disconnect-meta"} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/5">Disconnect</button>}
              <button type="button" onClick={connectMeta} disabled={!meta.configured || busy === "connect-meta"} className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-500 disabled:opacity-40">{busy === "connect-meta" ? "Opening Meta..." : meta.connected ? "Refresh connection" : "Connect Meta Business"}</button>
            </div>
          </div>
        </Panel>

        <Panel title="Account connections" detail="OAuth account links for YouTube, X, LinkedIn, and TikTok. Tokens stay encrypted on the backend.">
          <div className="grid gap-3 p-5 sm:grid-cols-2">
            {accountPlatforms.map((platform) => (
              <AccountConnectionCard
                key={platform}
                provider={platform}
                value={data?.providers?.[platform] || {}}
                busy={busy}
                onConnect={connectProvider}
                onDisconnect={disconnectProvider}
              />
            ))}
          </div>
        </Panel>
      </div>

      <div className="flex gap-1 overflow-x-auto rounded-xl border border-white/[.07] bg-[#121214] p-1.5" role="tablist" aria-label="Social command center views">
        {WORKSPACE_TABS.map(([key, label, Icon]) => (
          <button key={key} type="button" onClick={() => setWorkspace(key)} role="tab" aria-selected={workspace === key} className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-xs font-bold transition ${workspace === key ? "bg-blue-600 text-white" : "text-gray-400 hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {workspace === "workflow" && (
        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(360px,.8fr)_minmax(0,1.2fr)]">
            <Panel title={editingId ? "Edit draft" : "Create a draft"} detail="Every post enters the approval workflow before it can publish.">
              <form onSubmit={savePost} className="space-y-4 p-5">
                <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Internal title</span><input value={draft.title} onChange={(event) => updateDraft("title", event.target.value)} className="admin-field" placeholder="Summer launch announcement" maxLength={120} /></label>
                <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Caption *</span><textarea value={draft.caption} onChange={(event) => updateDraft("caption", event.target.value)} className="admin-field min-h-36 resize-y py-3" placeholder="Write the Facebook and Instagram caption..." maxLength={2200} required /></label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Campaign</span><input value={draft.campaign} onChange={(event) => updateDraft("campaign", event.target.value)} className="admin-field" placeholder="General" maxLength={100} /></label>
                  <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Publish date and time</span><input type="datetime-local" value={draft.scheduled_for} onChange={(event) => updateDraft("scheduled_for", event.target.value)} className="admin-field [color-scheme:dark]" /></label>
                </div>
                <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Optional link</span><div className="relative"><Link2 className="absolute left-3 top-3.5 h-4 w-4 text-gray-600" /><input type="url" value={draft.link_url} onChange={(event) => updateDraft("link_url", event.target.value)} className="admin-field pl-10" placeholder="https://showmelive.app/..." /></div></label>
                <div>
                  <span className="mb-2 block text-xs font-bold text-gray-300">Platforms *</span>
                  <div className="grid grid-cols-2 gap-2">
                    {["facebook", "instagram"].map((platform) => <label key={platform} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm font-bold ${draft.platforms.includes(platform) ? "border-blue-500/40 bg-blue-500/10 text-white" : "border-white/10 text-gray-500"}`}><input type="checkbox" checked={draft.platforms.includes(platform)} onChange={() => togglePlatform(platform)} className="accent-blue-600" />{platform === "facebook" ? <Facebook className="h-4 w-4 text-blue-400" /> : <Instagram className="h-4 w-4 text-pink-400" />}{platform === "facebook" ? "Facebook" : "Instagram"}</label>)}
                  </div>
                </div>
                <div>
                  <span className="mb-1.5 block text-xs font-bold text-gray-300">Image</span>
                  {draft.media_url && <div className="mb-2 overflow-hidden rounded-xl border border-white/10"><img src={draft.media_url} alt="Draft" className="max-h-56 w-full object-contain bg-black" /><button type="button" onClick={() => updateDraft("media_url", "")} className="w-full border-t border-white/[.06] py-2 text-xs font-bold text-red-300">Remove image</button></div>}
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[.025] px-4 py-3 text-xs font-bold text-gray-300 hover:bg-white/[.05]"><Upload className="h-4 w-4" />{busy === "media" ? "Uploading..." : draft.media_url ? "Replace image" : "Upload image"}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) uploadMedia(file); event.target.value = ""; }} /></label>
                </div>
                <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Internal notes</span><textarea value={draft.internal_notes} onChange={(event) => updateDraft("internal_notes", event.target.value)} className="admin-field min-h-20 resize-y py-3" placeholder="Notes for the approver; never published." maxLength={2000} /></label>
                <div className="flex gap-2">
                  {editingId && <button type="button" onClick={resetComposer} className="rounded-xl border border-white/10 px-4 py-3 text-sm font-bold text-gray-300">Cancel</button>}
                  <button type="submit" disabled={busy === "save-post" || !draft.caption.trim() || !draft.platforms.length} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-40"><Save className="h-4 w-4" />{busy === "save-post" ? "Saving..." : editingId ? "Save changes" : "Save draft"}</button>
                </div>
              </form>
            </Panel>

            <Panel title="Approval queue" detail="Posts publish only after an administrator approves them.">
              <div className="flex gap-1 overflow-x-auto border-b border-white/[.06] p-3">
                {STATUS_TABS.map(([key, label]) => <button key={key} type="button" onClick={() => setStatus(key)} className={`shrink-0 rounded-lg px-3 py-2 text-xs font-bold ${status === key ? "bg-white/10 text-white" : "text-gray-500 hover:text-gray-300"}`}>{label}<span className="ml-2 rounded-full bg-black/30 px-1.5 py-0.5 text-[10px]">{data?.counts?.[key] || 0}</span></button>)}
              </div>
              <div className="max-h-[820px] divide-y divide-white/[.06] overflow-y-auto">
                {visiblePosts.map((post) => (
                  <article key={post.id} className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1"><h3 className="truncate font-bold">{post.title}</h3><p className="mt-1 text-xs text-gray-500">{post.campaign || "General"} - {formatDate(post.scheduled_for || post.published_at)}</p></div>
                      <div className="flex gap-1">{(post.platforms || []).map((platform) => <PlatformPill key={platform} platform={platform} />)}</div>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-[96px_1fr]">
                      {post.media_url ? <img src={post.media_url} alt="" className="h-24 w-24 rounded-xl object-cover" /> : <div className="flex h-24 w-24 items-center justify-center rounded-xl bg-white/[.035] text-gray-700"><ImagePlus className="h-6 w-6" /></div>}
                      <p className="line-clamp-4 whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{post.caption}</p>
                    </div>
                    {post.failure_message && <p className="mt-3 rounded-lg bg-red-500/10 p-3 text-xs leading-relaxed text-red-300">{post.failure_message}</p>}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["draft", "failed"].includes(post.status) && <><button type="button" onClick={() => editPost(post)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-300 hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /> Edit</button><button type="button" onClick={() => runPostAction(post, "submit")} disabled={busy.startsWith(post.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-40"><Send className="h-3.5 w-3.5" /> Submit for approval</button></>}
                      {post.status === "awaiting_approval" && <><button type="button" onClick={() => runPostAction(post, "approve")} disabled={busy.startsWith(post.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-40"><ShieldCheck className="h-3.5 w-3.5" /> Approve</button><button type="button" onClick={() => runPostAction(post, "return-to-draft")} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-300">Return to Draft</button></>}
                      {["scheduled", "failed"].includes(post.status) && <button type="button" onClick={() => runPostAction(post, "return-to-draft")} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-300">Return to Draft</button>}
                      {post.status !== "published" && <button type="button" onClick={() => deletePost(post)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-500/20 px-3 py-2 text-xs font-bold text-red-300"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                    </div>
                  </article>
                ))}
                {!visiblePosts.length && <div className="px-5 py-16 text-center"><Clock3 className="mx-auto h-8 w-8 text-gray-700" /><p className="mt-3 text-sm font-semibold text-gray-500">No {STATUS_TABS.find(([key]) => key === status)?.[1].toLowerCase()} posts.</p></div>}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {workspace === "calendar" && (
        <Panel title="Content calendar" detail="Approved scheduled and published posts, grouped by publication date." action={<div className="flex items-center gap-2"><button type="button" onClick={() => setCalendarMonth((value) => new Date(value.getFullYear(), value.getMonth() - 1, 1))} className="rounded-lg border border-white/10 p-2"><ChevronLeft className="h-4 w-4" /></button><span className="min-w-32 text-center text-sm font-bold">{calendarMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span><button type="button" onClick={() => setCalendarMonth((value) => new Date(value.getFullYear(), value.getMonth() + 1, 1))} className="rounded-lg border border-white/10 p-2"><ChevronRight className="h-4 w-4" /></button></div>}>
          <div className="divide-y divide-white/[.06]">
            {calendarPosts.map((post) => <article key={post.id} className="grid gap-4 p-5 sm:grid-cols-[100px_1fr_auto] sm:items-center"><div><p className="text-2xl font-black text-blue-400">{new Date(post.scheduled_for || post.published_at).getDate()}</p><p className="text-[10px] font-bold uppercase text-gray-500">{new Date(post.scheduled_for || post.published_at).toLocaleDateString(undefined, { weekday: "short" })}</p></div><div><p className="font-bold">{post.title}</p><p className="mt-1 text-xs text-gray-500">{formatDate(post.scheduled_for || post.published_at)} - {post.campaign}</p></div><div className="flex gap-1">{post.platforms.map((platform) => <PlatformPill key={platform} platform={platform} />)}</div></article>)}
            {!calendarPosts.length && <p className="p-12 text-center text-sm text-gray-500">No scheduled or published posts in this month.</p>}
          </div>
        </Panel>
      )}

      {workspace === "previews" && (
        <Panel title="Platform previews" detail="Live preview of the current composer. Exact cropping and text wrapping may vary inside Meta apps.">
          <div className="grid gap-8 bg-[#0c0c0d] p-5 lg:grid-cols-2 lg:p-8"><div><p className="mb-4 text-center text-xs font-black uppercase tracking-wider text-blue-400">Facebook Page</p><PlatformPreview platform="facebook" draft={draft} settings={data?.settings} /></div><div><p className="mb-4 text-center text-xs font-black uppercase tracking-wider text-pink-400">Instagram</p><PlatformPreview platform="instagram" draft={draft} settings={data?.settings} /></div></div>
        </Panel>
      )}

      {workspace === "analytics" && (
        <Panel title="Campaign analytics" detail="Engagement totals collected from connected Meta business accounts." action={<button type="button" onClick={syncMeta} disabled={!meta.connected || busy === "sync-meta"} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-40"><RefreshCw className={`h-4 w-4 ${busy === "sync-meta" ? "animate-spin" : ""}`} /> Sync Meta</button>}>
          <div className="overflow-x-auto"><table className="w-full min-w-[720px] text-sm"><thead className="bg-white/[.025] text-left text-[10px] font-black uppercase tracking-wider text-gray-500"><tr><th className="px-5 py-3">Campaign</th><th className="px-5 py-3">Posts</th><th className="px-5 py-3">Published</th><th className="px-5 py-3">Failed</th><th className="px-5 py-3">Likes</th><th className="px-5 py-3">Comments</th><th className="px-5 py-3">Shares</th></tr></thead><tbody className="divide-y divide-white/[.06]">{(data?.campaign_analytics || []).map((row) => <tr key={row.campaign}><td className="px-5 py-4 font-bold">{row.campaign}</td><td className="px-5 py-4 text-gray-400">{row.posts}</td><td className="px-5 py-4 text-green-400">{row.published}</td><td className="px-5 py-4 text-red-300">{row.failed}</td><td className="px-5 py-4">{row.likes}</td><td className="px-5 py-4">{row.comments}</td><td className="px-5 py-4">{row.shares}</td></tr>)}{!(data?.campaign_analytics || []).length && <tr><td colSpan={7} className="p-12 text-center text-gray-500">Campaign analytics will appear after posts are created.</td></tr>}</tbody></table></div>
        </Panel>
      )}

      {workspace === "comments" && (
        <Panel title="Comment review" detail="Review comments synchronized from published Facebook and Instagram posts." action={<div className="flex gap-2"><select value={commentFilter} onChange={(event) => setCommentFilter(event.target.value)} className="rounded-lg border border-white/10 bg-[#18181b] px-3 py-2 text-xs font-bold outline-none"><option value="unreviewed">Unreviewed</option><option value="flagged">Flagged</option><option value="reviewed">Reviewed</option><option value="resolved">Resolved</option><option value="all">All comments</option></select><button type="button" onClick={syncMeta} disabled={!meta.connected || busy === "sync-meta"} className="rounded-lg bg-blue-600 p-2 text-white disabled:opacity-40" aria-label="Sync Meta comments"><RefreshCw className={`h-4 w-4 ${busy === "sync-meta" ? "animate-spin" : ""}`} /></button></div>}>
          <div className="divide-y divide-white/[.06]">{filteredComments.map((comment) => <article key={comment.id} className="p-5"><div className="flex flex-wrap items-start justify-between gap-3"><div><div className="flex items-center gap-2"><PlatformPill platform={comment.platform} /><span className="text-sm font-bold">{comment.author_name}</span></div><p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{comment.message}</p><p className="mt-2 text-[11px] text-gray-600">{formatDate(comment.created_at)} - {comment.like_count || 0} likes</p></div><span className="rounded-full bg-white/[.06] px-2.5 py-1 text-[10px] font-bold uppercase text-gray-400">{comment.review_status}</span></div><div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={() => reviewComment(comment, "reviewed")} className="rounded-lg border border-green-500/20 px-3 py-2 text-xs font-bold text-green-300">Mark reviewed</button><button type="button" onClick={() => reviewComment(comment, "flagged")} className="rounded-lg border border-amber-500/20 px-3 py-2 text-xs font-bold text-amber-300">Flag</button><button type="button" onClick={() => reviewComment(comment, "resolved")} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-300">Resolve</button></div></article>)}{!filteredComments.length && <p className="p-12 text-center text-sm text-gray-500">No comments in this review queue. Use Sync Meta to check for new activity.</p>}</div>
        </Panel>
      )}

      {workspace === "brand" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <Panel title="Brand settings" detail="Defaults shared across the social publishing workflow.">
            <form onSubmit={saveBrand} className="grid gap-4 p-5 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Brand name</span><input value={brandDraft.brand_name || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, brand_name: event.target.value }))} className="admin-field" /></label>
              <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Default link</span><input type="url" value={brandDraft.default_link_url || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, default_link_url: event.target.value }))} className="admin-field" /></label>
              <label className="block sm:col-span-2"><span className="mb-1.5 block text-xs font-bold text-gray-300">Brand voice</span><textarea value={brandDraft.brand_voice || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, brand_voice: event.target.value }))} className="admin-field min-h-24 resize-y py-3" /></label>
              <label className="block sm:col-span-2"><span className="mb-1.5 block text-xs font-bold text-gray-300">Default hashtags</span><input value={brandDraft.default_hashtags || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, default_hashtags: event.target.value }))} className="admin-field" /></label>
              <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Timezone</span><input value={brandDraft.timezone || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, timezone: event.target.value }))} className="admin-field" /></label>
              {meta.available_pages?.length > 0 && <label className="block"><span className="mb-1.5 block text-xs font-bold text-gray-300">Publishing Page</span><select value={brandDraft.active_meta_page_id || meta.page_id || ""} onChange={(event) => setBrandDraft((value) => ({ ...value, active_meta_page_id: event.target.value }))} className="admin-field">{meta.available_pages.map((page) => <option key={page.id} value={page.id}>{page.name}{page.instagram_username ? ` / @${page.instagram_username}` : ""}</option>)}</select></label>}
              <div className="flex items-center gap-3 rounded-xl border border-blue-500/20 bg-blue-500/[.07] p-4 sm:col-span-2"><ShieldCheck className="h-5 w-5 shrink-0 text-blue-400" /><div><p className="text-sm font-bold">Approval is always required</p><p className="mt-1 text-xs text-gray-500">No draft can bypass Awaiting approval.</p></div></div>
              <button type="submit" disabled={busy === "brand"} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white sm:col-span-2"><Save className="h-4 w-4" />{busy === "brand" ? "Saving..." : "Save brand settings"}</button>
            </form>
          </Panel>
          <Panel title="Meta approval readiness" detail="Production publishing depends on Meta's business review and permission approval.">
            <div className="space-y-3 p-5 text-xs leading-relaxed text-gray-400">
              <p className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">Use a verified Meta Business portfolio, a managed Facebook Page, and a linked Instagram professional account.</p>
              <p className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">Meta must approve the requested Page and Instagram publishing, engagement, and comment permissions before non-test users can operate the connection.</p>
              <p className="rounded-xl border border-white/[.07] bg-white/[.025] p-4">The connection stores Page access tokens encrypted. Tokens are never returned to the browser.</p>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
};

export default SocialMediaCommandCenter;
